import { BOAT_MONEY_KEYS, stripMoneyKeys } from '@/modules/inventory/lib/moneyFields';
import type { WizardFormData } from '../types/wizard.types';

/**
 * Pure builder originally shared by the removed AddBoatWizard and LiveFillCanvas so both emitted an
 * identical create/publish payload. Extracted verbatim (behavior-preserving).
 *
 * #1408 (D-08) — `canViewCost` is the viewer's finance.cost.view: without it the
 * payload carries no money key and no equipment cost (a stale form value or a
 * derived total_cost would otherwise meet the server's 422).
 */
export function buildBoatPayload(values: WizardFormData, canViewCost: boolean) {
  const payload = {
    condition: values.condition,
    manufacturer: values.manufacturer,
    model: values.model,
    model_year: values.model_year,
    length_ft: values.length_ft,
    beam_in: values.beam_in,
    draft_in: values.draft_in,
    seating_capacity: values.seating_capacity,
    exterior_color: values.exterior_color || undefined,
    interior_color: values.interior_color || undefined,
    hin_number: values.hin_number || undefined,
    fuel_type: values.fuel_type || undefined,
    boat_types: values.boat_types ?? [],
    engine_type: values.engine_type || undefined,
    engines: values.engines?.filter(
      (e) => e.engine_manufacturer || e.engine_model || e.engine_horsepower || e.engine_hours
    ) ?? [],
    standard_equipment: values.standard_equipment,
    optional_equipment: values.optional_equipment,
    // Trailer persists as a nested inventory_trailers asset: the backend
    // Store/Update requests accept a `trailers[]` array (max 1), NOT flat
    // `trailer_*` keys — those columns were dropped from inventory_boats. Emit a
    // single-element array only when the dealer entered any trailer spec;
    // otherwise send none so no empty trailer is created.
    //
    // Trailer PRICING is not sent from the boat at all (#515): it is owned by the
    // trailer asset. The boat invoice sources the attached trailer's cost from the
    // linked asset, not a boat field.
    trailers:
      values.trailer_vin ||
      values.trailer_make ||
      values.trailer_model ||
      values.trailer_axles
        ? [
            {
              trailer_vin: values.trailer_vin || undefined,
              trailer_make: values.trailer_make || undefined,
              trailer_model: values.trailer_model || undefined,
              trailer_axles: values.trailer_axles || undefined,
            },
          ]
        : [],
    description: values.description || undefined,
    short_description: values.short_description || undefined,
    // #607 — creation NEVER publishes to the website. Publishing is a separate,
    // deliberate action so a listing cannot reach WooCommerce before its AI
    // content exists. `||` here meant any falsy value published the boat.
    website_status: values.website_status ?? 'disabled',
    status_id: values.status_id || undefined,
    first_available_date: values.first_available_date || undefined,
    ribbon_id: values.ribbon_id || undefined,
    ribbon_expiry_type: values.ribbon_expiry_type || undefined,
    ribbon_expiry_value: values.ribbon_expiry_value || undefined,
    our_price: values.our_price ?? 0,
    msrp: values.msrp,
    regular_price: values.regular_price,
    sale_price: values.sale_price,
    cost_price: values.cost_price,
    is_on_sale: values.is_on_sale,
    boat_cost_price: values.boat_cost_price,
    boat_retail_price: values.boat_retail_price,
    invoice_amount: values.invoice_amount,
    rigging: values.rigging,
    freight: values.freight,
    total_cost: (Number(values.invoice_amount) || 0) + (Number(values.rigging) || 0) + (Number(values.freight) || 0) || undefined,
    location_ids: values.location_ids,
    primary_location_id: values.primary_location_id,
  };
  return stripMoneyKeys(payload, BOAT_MONEY_KEYS, canViewCost);
}
